import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { toast, shake } from '@tonus/toast';
class TonusCardChooser extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.he = document.documentElement.lang.startsWith('he');
    this.form = this.querySelector('form');
    this.input = this.form.elements.quantity;
    this.add = this.form.querySelector('[type=submit]');
    this.form.hidden = false;
    this.querySelector('.tn-card-chooser__price').hidden = false;
    /* `tn-purchase-open` stands in for what used to be read in CSS as
       `:has(.tn-card-chooser__purchase:not([hidden]))`. That selector made the
       `hidden` attribute a document-wide style dependency, so revealing the
       form on each of eighteen cards invalidated every element on the page.
       Measured before: sixty-plus style recalculations of 47-107ms, each over
       1638 elements. A class on the card invalidates only the card. */
    this.closest('product-card')?.classList.add('tn-has-color-choice', 'tn-purchase-open');
    this.updatePurchase();
    this.selectOnlyShade();
    const details = this.closest('product-card')?.querySelector('.tonus-card-action');
    if (details) {
      details.removeAttribute('aria-label');
      details.querySelector('span').textContent = this.he ? 'פרטי המוצר' : 'Product details';
    }
    /* `beforetoggle` runs before the panel is shown, so anyone who opens it
       ahead of the idle fill still finds a complete list. */
    this.querySelector('.tn-card-chooser__shades')
      ?.addEventListener('beforetoggle', event => {
        if (event.newState !== 'open') return;
        this.fillMoreColors();
        this.placeShades();
      }, {signal:this.controller.signal});
    this.input.addEventListener('input', () => this.updatePurchase(), {signal:this.controller.signal});
    this.form.addEventListener('submit', event => { event.preventDefault(); event.stopPropagation(); this.purchase(); }, {signal:this.controller.signal});
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      event.promise?.then(({detail}) => {
        if (!detail?.items) return;
        this.querySelectorAll('[data-choice-id]').forEach(shade => {
          shade.dataset.choiceCart = detail.items.filter(item => String(item.variant_id) === shade.dataset.choiceId).reduce((sum,item) => sum + item.quantity,0);
        });
        this.showCartCount();
      }).catch(() => {});
    }, {signal:this.controller.signal});
    this.addEventListener('click', event => {
      event.stopPropagation(); // Keep native card navigation out of the chooser.
      const change = event.target.closest('[data-quantity-change]');
      if (change) {
        if (this.refuse(change.closest('.tn-card-chooser__quantity'), this.choiceReason())) return;
        const min = this.minQuantity || 1;
        const step = Number(this.input.step) || 1;
        const max = Number(this.input.max) || Infinity;
        const value = Number(this.input.value) || 0;
        const up = Number(change.dataset.quantityChange) > 0;
        /* Stepping up out of nothing lands on the smallest quantity the variant
           actually sells, and stepping down out of it returns to nothing. */
        let next = up ? (value < min ? min : value + step) : (value <= min ? 0 : value - step);
        this.input.value = Math.max(0, Math.min(max, next));
        this.updatePurchase();
        return;
      }
      const shade = event.target.closest('[data-choice-name]');
      if (!shade || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
      if (!this.closest('product-card')) return;
      event.preventDefault();
      event.stopPropagation();
      this.applyChoice(shade);
      /* Picking from the full palette answers the question it was opened for. */
      const panel = this.querySelector('.tn-card-chooser__shades');
      if (panel?.matches(':popover-open')) panel.hidePopover();
    }, {signal:this.controller.signal});
  }
  updatePurchase() {
    const shade = this.selected;
    const available = shade?.dataset.choiceAvailable === 'true';
    const exact = shade?.dataset.choiceCount === '1';
    const enough = Number(this.input.value) >= (this.minQuantity || 1);
    const blocked = this.busy || !shade || !available || !exact || !enough;
    /* aria-disabled, not disabled: a disabled button receives no click, and a
       refused press has to be answered with a judder and a message. The markup
       ships the button disabled so it is inert until this component is live. */
    this.add.disabled = false;
    this.add.setAttribute('aria-disabled', String(blocked));
    this.input.readOnly = blocked;
    /* The label stays the real action. Only the colour makes it live. */
    this.add.textContent = this.busy ? (this.he ? 'מוסיפים…' : 'Adding…') : (this.he ? 'הוספה לסל' : 'Add to cart');
  }
  /**
   * Why the shade itself cannot be bought, if it cannot.
   * @returns {string|null} in the page language
   */
  choiceReason() {
    const shade = this.selected;
    if (!shade) return this.he ? 'אנא בחרו צבע' : 'Please choose a color';
    if (shade.dataset.choiceAvailable !== 'true') return this.he ? 'הצבע אזל כרגע' : 'That color is sold out';
    if (shade.dataset.choiceCount !== '1') return this.he ? 'בחרו אפשרויות בפרטי המוצר' : 'Choose options in product details';
    return null;
  }
  /**
   * Everything standing between this card and an add, the quantity included.
   * The quantity stepper deliberately asks `choiceReason` instead: refusing a
   * press on the quantity because the quantity is too low would leave no way
   * to raise it.
   * @returns {string|null} in the page language
   */
  blockedReason() {
    const reason = this.choiceReason();
    if (reason) return reason;
    if (Number(this.input.value) < (this.minQuantity || 1)) {
      return this.he ? 'בחרו כמות להוספה' : 'Choose a quantity to add';
    }
    return null;
  }
  /**
   * Answers a refused press on `element`.
   * @param {Element|null} element
   * @param {string|null} [reason] defaults to every reason there is
   * @returns {boolean} true when refused
   */
  refuse(element, reason = this.blockedReason()) {
    if (this.busy) return true;
    if (!reason) return false;
    shake(element);
    toast(reason, {tone:'alert', anchor: element});
    return true;
  }
  showCartCount() {
    return Number(this.selected?.dataset.choiceCart || 0);
  }
  async purchase() {
    if (this.refuse(this.add)) return;
    if (!this.form.reportValidity()) { shake(this.form.querySelector('.tn-card-chooser__quantity')); return; }
    const shade = this.selected;
    const id = shade.dataset.choiceId;
    const quantity = Number(this.input.value);
    this.busy = true;
    this.updatePurchase();
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.dispatchEvent(new CartLinesUpdateEvent({action:'add', context:'product', lines:[{merchandiseId:id,quantity}], promise:deferred.promise}));
    let added = false;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0,5);
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/add.js', {method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'}, body:JSON.stringify({items:[{id,quantity}],sections,sections_url:location.pathname})});
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : (this.he ? 'לא ניתן להוסיף את הכמות המבוקשת.' : 'Unable to add the requested quantity.'));
      added = true;
      const cartResponse = await fetch(root + 'cart.js', {headers:{Accept:'application/json'},cache:'no-store'});
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,sections:result.sections,source:'tonus-card-chooser',didError:false,itemCount:quantity}});
      const inCart = cart.items.filter(item => String(item.variant_id) === id).reduce((sum, item) => sum + item.quantity, 0);
      toast(this.he
        ? `נוספו ${quantity} בצבע ${shade.dataset.choiceName} · בסל: ${inCart}`
        : `Added ${quantity} in ${shade.dataset.choiceName} · in cart: ${inCart}`,
        {anchor: this.add});
      /* Back to nothing once it is in the cart. The next add is a fresh
         decision, not a repeat of the last one left sitting in the field. */
      this.input.value = '0';
    } catch(error) {
      // Shopify can partially add inventory even when it returns 422.
      // Always reconcile the real cart before reporting the rejected quantity.
      try {
        const refresh = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {cache:'no-store'});
        if (!refresh.ok) throw new Error('Cart refresh failed');
        const cart = await refresh.json();
        deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,source:'tonus-card-chooser',didError:true}});
      } catch { deferred.reject(error); }
      const message = added
        ? (this.he ? 'המוצר נוסף, בדקו את הסל לפני הוספה נוספת' : 'Item added. Check your cart before adding again')
        : (error instanceof TypeError
          ? (this.he ? 'החיבור נקטע, בדקו את הסל לפני ניסיון נוסף' : 'Connection interrupted. Check your cart before retrying')
          : error.message);
      const offerCart = added || error instanceof TypeError;
      toast(message, {
        tone: 'alert',
        anchor: this.add,
        href: offerCart ? (window.Shopify?.routes?.root || '/') + 'cart' : undefined,
        hrefLabel: offerCart ? (this.he ? 'מעבר לסל' : 'View cart') : undefined
      });
    } finally {
      this.busy = false;
      this.updatePurchase();
    }
  }
  /**
   * Everything that follows from a shade being chosen. Shared by a real click
   * and by the automatic choice a one-shade product makes for itself.
   *
   * @param {HTMLElement} shade
   */
  applyChoice(shade) {
    const card = this.closest('product-card');
    if (!card) return;
    this.promoteShade(shade);
    this.querySelectorAll('[data-choice-name]').forEach(link => {
      if (link === shade) link.setAttribute('aria-current','true');
      else link.removeAttribute('aria-current');
    });
    const he = this.he;
    const unavailable = shade.dataset.choiceAvailable !== 'true';
    this.selected = shade;
    if (document.body.classList.contains('template-index')) {
      this.classList.add('tn-home-selected');
      if (!unavailable && Number(this.input.value) === 0) this.input.value = shade.dataset.choiceMin || '1';
    }
    /* The field's own floor stays 0 so "none yet" remains a real, valid state.
       The variant's true minimum is kept beside it and enforced on the stepper
       and on the add, rather than by pre-filling a quantity nobody asked for. */
    this.minQuantity = Number(shade.dataset.choiceMin) || 1;
    this.input.min = '0';
    this.input.step = shade.dataset.choiceStep || '1';
    this.input.max = shade.dataset.choiceMax || '';
    this.updatePurchase();
    this.showCartCount();
    this.querySelector('[data-choice-status]').textContent = shade.dataset.choiceName + (unavailable ? (he ? ' · אזל כרגע' : ' · Sold out') : '');
    this.querySelector('[data-choice-price-output]').textContent = shade.dataset.choicePrice;
    const compare = this.querySelector('[data-choice-compare-output]');
    compare.hidden = !shade.dataset.choiceCompare;
    compare.textContent = shade.dataset.choiceCompare || '';
    this.querySelector('.tn-card-chooser__price').hidden = false;
    card.classList.add('tn-has-color-choice');
    card.querySelectorAll('.tonus-card-action,.tn-card-copy,.tn-card-media__link').forEach(link => { link.href = shade.href; });
    const cta = card.querySelector('.tonus-card-action');
    if (cta) {
      cta.setAttribute('aria-label', (he ? 'למוצר בצבע ' : 'View product in ') + shade.dataset.choiceName);
      const label = cta.querySelector('span');
      if (label) label.textContent = he ? 'פרטי המוצר' : 'Product details';
    }
    const image = card.querySelector('.tn-card-media__photo');
    if (image && shade.dataset.choiceImage) {
      image.src = shade.dataset.choiceImage;
      image.removeAttribute('srcset');
      image.alt = shade.dataset.choiceName;
    }
  }
  /**
   * A product with one shade has nothing to choose. Asking for a choice there
   * is a step that cannot go any other way, so the card makes it on arrival
   * and the button is live immediately.
   */
  selectOnlyShade() {
    const shades = this.querySelectorAll('[data-choice-name]');
    if (shades.length !== 1) return;
    this.applyChoice(shades[0]);
  }
  /**
   * A shade chosen from the full palette takes the first place in the short row
   * under "choose a colour", so the card goes on showing what was picked
   * instead of sending you back into the palette to find it again. The row
   * stays six wide: whatever falls off the end returns to the palette.
   *
   * @param {HTMLElement} shade
   */
  promoteShade(shade) {
    const row = this.querySelector(':scope > .tn-card-chooser__colors');
    const pool = this.querySelector('[data-more-colors]');
    if (!row || !pool || shade.parentElement === row) return;
    row.prepend(shade);
    while (row.children.length > 6) pool.prepend(row.lastElementChild);
  }
  /**
   * Sits the palette against the button that opened it.
   *
   * A popover lives in the top layer, which is what lets it escape the card's
   * `overflow: hidden` and leave the grid alone, but it also means the browser
   * centres it in the viewport until it is told otherwise. Anchor positioning
   * in CSS is not on every browser yet, so this is measured here.
   */
  placeShades() {
    const panel = this.querySelector('.tn-card-chooser__shades');
    const button = this.querySelector('.tn-card-chooser__more');
    if (!panel || !button) return;
    const gap = 8;
    const edge = 12;
    requestAnimationFrame(() => {
      const anchor = button.getBoundingClientRect();
      const width = panel.offsetWidth;
      const height = panel.offsetHeight;
      const viewportW = document.documentElement.clientWidth;
      const viewportH = document.documentElement.clientHeight;
      let left = anchor.left + anchor.width / 2 - width / 2;
      left = Math.min(Math.max(left, edge), viewportW - width - edge);
      /* Below the button, and above it when the foot of the window is closer. */
      let top = anchor.bottom + gap;
      if (top + height > viewportH - edge) {
        const above = anchor.top - height - gap;
        top = above >= edge ? above : Math.max(edge, viewportH - height - edge);
      }
      panel.style.left = `${Math.round(left)}px`;
      panel.style.top = `${Math.round(top)}px`;
    });
  }
  /**
   * Moves the remaining shades out of their template and into the disclosure.
   * Idempotent, and cheap to call again.
   */
  fillMoreColors() {
    const template = this.querySelector('template[data-more-colors-source]');
    if (!template) return;
    const target = this.querySelector('[data-more-colors]');
    if (target) target.append(template.content.cloneNode(true));
    template.remove();
  }
  disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('tonus-card-chooser')) customElements.define('tonus-card-chooser',TonusCardChooser);

/* Background fill, once the page the visitor is actually on has finished.
   All the cards are filled inside one idle callback on purpose. Spreading them
   over an idle callback each was measured worse, not better: the page carries
   `:has()` rules, so every separate insertion invalidated the whole document
   and cost its own style recalculation. Eleven cards meant eleven recalcs of
   roughly 65ms. Writing them together, with no layout read in between, lets the
   browser coalesce the lot into one. */
{
  const fillAll = () => {
    for (const chooser of document.querySelectorAll('tonus-card-chooser')) chooser.fillMoreColors?.();
  };
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
  const schedule = () => idle(fillAll, { timeout: 4000 });
  if (document.readyState === 'complete') schedule();
  else addEventListener('load', schedule, { once: true });
}
